import { Course, UserProgress, Module } from '../types/course';
import { courses } from '../data/courses';

class CourseService {
  // Get all available courses
  getCourses(): Course[] {
    return courses;
  }

  // Get course by ID
  getCourseById(courseId: string): Course | undefined {
    return courses.find(course => course.id === courseId);
  }

  // Get courses by category
  getCoursesByCategory(category: string): Course[] {
    return courses.filter(course => course.category === category);
  }

  // Get required courses
  getRequiredCourses(): Course[] {
    return courses.filter(course => course.isRequired);
  }

  // Get courses by level
  getCoursesByLevel(level: string): Course[] {
    return courses.filter(course => course.level === level);
  }

  // Search courses
  searchCourses(query: string): Course[] {
    const lowercaseQuery = query.toLowerCase();
    return courses.filter(course => 
      course.title.toLowerCase().includes(lowercaseQuery) ||
      course.description.toLowerCase().includes(lowercaseQuery) ||
      course.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
    );
  }

  // Get user progress for a course
  getUserProgress(userId: string, courseId: string): UserProgress | null {
    // In a real application, this would fetch from a database
    const stored = localStorage.getItem(`progress_${userId}_${courseId}`);
    return stored ? JSON.parse(stored) : null;
  }

  // Update user progress
  updateUserProgress(progress: UserProgress): void {
    // In a real application, this would save to a database
    localStorage.setItem(
      `progress_${progress.userId}_${progress.courseId}`,
      JSON.stringify(progress)
    );
  }

  // Get module by ID within a course
  getModuleById(courseId: string, moduleId: string): Module | undefined {
    const course = this.getCourseById(courseId);
    return course?.modules.find(module => module.id === moduleId);
  }

  // Calculate course completion percentage
  calculateCompletionPercentage(userId: string, courseId: string): number {
    const progress = this.getUserProgress(userId, courseId);
    const course = this.getCourseById(courseId);
    
    if (!progress || !course) return 0;
    
    const totalModules = course.modules.length;
    const completedModules = progress.completedModules.length;
    
    return Math.round((completedModules / totalModules) * 100);
  }

  // Check if user can access a course (prerequisites)
  canAccessCourse(userId: string, courseId: string): boolean {
    const course = this.getCourseById(courseId);
    if (!course || !course.prerequisites) return true;

    // Check if all prerequisite courses are completed
    return course.prerequisites.every(prereqId => {
      const progress = this.getUserProgress(userId, prereqId);
      return progress?.status === 'completed';
    });
  }

  // Get recommended courses based on user progress
  getRecommendedCourses(userId: string): Course[] {
    const accessibleCourses = courses.filter(course => 
      this.canAccessCourse(userId, course.id)
    );

    // Prioritize required courses that haven't been started
    const requiredNotStarted = accessibleCourses.filter(course => {
      const progress = this.getUserProgress(userId, course.id);
      return course.isRequired && (!progress || progress.status === 'not_started');
    });

    if (requiredNotStarted.length > 0) {
      return requiredNotStarted;
    }

    // Then suggest courses in progress
    const inProgress = accessibleCourses.filter(course => {
      const progress = this.getUserProgress(userId, course.id);
      return progress?.status === 'in_progress';
    });

    if (inProgress.length > 0) {
      return inProgress;
    }

    // Finally, suggest other accessible courses
    return accessibleCourses.filter(course => {
      const progress = this.getUserProgress(userId, course.id);
      return !progress || progress.status === 'not_started';
    }).slice(0, 3);
  }

  // Initialize user progress for a course
  initializeCourseProgress(userId: string, courseId: string): UserProgress {
    const progress: UserProgress = {
      courseId,
      userId,
      status: 'not_started',
      completedModules: [],
      timeSpent: 0,
      attentionScore: 100,
      assessmentScores: {},
      lastAccessed: new Date()
    };

    this.updateUserProgress(progress);
    return progress;
  }

  // Mark module as completed
  completeModule(userId: string, courseId: string, moduleId: string): void {
    let progress = this.getUserProgress(userId, courseId);
    
    if (!progress) {
      progress = this.initializeCourseProgress(userId, courseId);
    }

    if (!progress.completedModules.includes(moduleId)) {
      progress.completedModules.push(moduleId);
      progress.status = 'in_progress';
      progress.lastAccessed = new Date();

      // Check if course is completed
      const course = this.getCourseById(courseId);
      if (course && progress.completedModules.length === course.modules.length) {
        progress.status = 'completed';
        progress.completionDate = new Date();
      }

      this.updateUserProgress(progress);
    }
  }

  // Add time spent to course
  addTimeSpent(userId: string, courseId: string, minutes: number): void {
    let progress = this.getUserProgress(userId, courseId);
    
    if (!progress) {
      progress = this.initializeCourseProgress(userId, courseId);
    }

    progress.timeSpent += minutes;
    progress.lastAccessed = new Date();
    this.updateUserProgress(progress);
  }

  // Update attention score
  updateAttentionScore(userId: string, courseId: string, score: number): void {
    let progress = this.getUserProgress(userId, courseId);
    
    if (!progress) {
      progress = this.initializeCourseProgress(userId, courseId);
    }

    // Calculate weighted average (70% existing, 30% new)
    progress.attentionScore = Math.round(
      (progress.attentionScore * 0.7) + (score * 0.3)
    );
    progress.lastAccessed = new Date();
    this.updateUserProgress(progress);
  }

  // Record assessment score
  recordAssessmentScore(userId: string, courseId: string, assessmentId: string, score: number): void {
    let progress = this.getUserProgress(userId, courseId);
    
    if (!progress) {
      progress = this.initializeCourseProgress(userId, courseId);
    }

    progress.assessmentScores[assessmentId] = score;
    progress.lastAccessed = new Date();
    this.updateUserProgress(progress);
  }

  // Get course statistics
  getCourseStatistics(courseId: string): {
    totalEnrollments: number;
    completionRate: number;
    averageScore: number;
    averageTimeSpent: number;
  } {
    // In a real application, this would aggregate data from the database
    // For demo purposes, return mock statistics
    return {
      totalEnrollments: Math.floor(Math.random() * 500) + 100,
      completionRate: Math.floor(Math.random() * 30) + 70,
      averageScore: Math.floor(Math.random() * 20) + 75,
      averageTimeSpent: Math.floor(Math.random() * 60) + 120
    };
  }
}

export const courseService = new CourseService();